import React, { useState, useRef, useEffect } from "react";
import "./UserProfileSidebar.scss";
import UserIcon from "components/icons/UserIcon";
import { UserProfile } from "interfaces/IUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { updateUserProfile } from "../../../../api/auth";
import { createAuthenticatedAxiosInstance } from "../../../../api/auth";
import { useAuthStore } from "../../../../zustand/loginStore";
import { useUserStore } from "../../../../zustand/userStore";
import { PublicCategory } from "interfaces/ICategory";
import { Size } from "interfaces/ISize";

/**
 * Enum para las pestañas del perfil de usuario
 */
export enum ProfileTab {
  PROFILE = "profile",
  ORDERS = "orders",
  FAVORITES = "favorites",
}
/**
 * Type guard for the profile tabs
 */
export const isValidProfileTab = (value: any): value is ProfileTab => {
  return Object.values(ProfileTab).includes(value);
}
/**
 * Props para el componente UserProfileSidebar
 */
interface UserProfileSidebarProps {
  userData: UserProfile | null;
  activeTab: ProfileTab;
  setActiveTab: (tab: ProfileTab) => void;
  onTabClick: (profileTab: ProfileTab) => void;
  onProfileUpdate?: (updatedProfile: UserProfile) => void;
}

/**
 * Componente sidebar para navegación en el perfil de usuario
 * Incluye foto de perfil y opciones de navegación
 */
const UserProfileSidebar: React.FC<UserProfileSidebarProps> = ({ 
  userData, 
  activeTab,
  setActiveTab,
  onTabClick,
  onProfileUpdate 
}) => {

  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { token } = useAuthStore();
  const { imageTimestamp, setImageTimestamp } = useUserStore();

  // Determinar si el usuario tiene una imagen de perfil
  const hasProfileImage = userData && userData.image_url;

  const handleImageClick = () => {
    // Activar el input de archivo oculto
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file || !userData) return;
    
    // Verificar tipo de archivo
    const fileType = file.type;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(fileType)) {
      alert('Solo se permiten archivos JPG, JPEG o PNG');
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Convertir la imagen a base64
      const base64Image = await convertToBase64(file);
      
      // Crear objeto de datos para la actualización
      const updateData = {
        name: userData.name,
        email: userData.email,
        phone: userData.phone || '',
        image: base64Image
      };
      
      // Enviar la actualización al servidor
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const updatedProfile = await updateUserProfile(axiosInstance, updateData);
      
      // Notificar al componente padre sobre la actualización
      if (onProfileUpdate && updatedProfile) {
        onProfileUpdate(updatedProfile.user);
      }
      
      // También actualizar el store de autenticación para que el navbar se actualice
      if (updatedProfile && updatedProfile.user) {
        // Actualizar el store de autenticación con los nuevos datos
        const { setUserData } = useAuthStore.getState();
        setUserData(updatedProfile.user);
        
        // Forzar la actualización de la imagen actualizando el timestamp
        setImageTimestamp(Date.now());
      }
      
    } catch (error) {
      console.error("Error al subir la imagen:", error);
      alert("No se pudo actualizar la imagen de perfil. Inténtelo de nuevo.");
    } finally {
      setIsUploading(false);
    }
  };

  // Función para convertir un archivo a base64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Extraer solo la parte base64 sin el prefijo (data:image/jpeg;base64,)
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  /**
   * This use effects loads the outlet based on the active tab
   */
  useEffect(() => {
    onTabClick(activeTab);
  },[activeTab]);

  return (
    <div className="user-profile-sidebar">
      {/* Input de archivo oculto */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".jpg,.jpeg,.png"
        onChange={handleFileChange}
      />
      
      {/* Avatar del usuario */}
      <div className="profile-avatar-container">
        <div className="profile-avatar">
          {hasProfileImage ? (
            <img
              src={`${userData?.image_url}?t=${imageTimestamp}`}
              alt="Foto de perfil"
              className="user-photo"
            />
          ) : (
            <UserIcon
              size="80px"
              color="#777777"
              className="user-placeholder"
            />
          )}
        </div>
        <button 
          className="camera-button" 
          onClick={handleImageClick}
          disabled={isUploading}
        >
          {isUploading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            <FontAwesomeIcon icon={faCamera} className="camera-icon" />
          )}
        </button>
      </div>

      {/* Navegación del perfil */}
      <div className="profile-navigation">
        <button
          className={`nav-button ${
            activeTab === ProfileTab.PROFILE ? "active" : ""
          }`}
          onClick={() => setActiveTab(ProfileTab.PROFILE)}
        >
          Mi perfil
        </button>

        <button
          className={`nav-button ${
            activeTab === ProfileTab.ORDERS ? "active" : ""
          }`}
          onClick={() => setActiveTab(ProfileTab.ORDERS)}
        >
          Mis pedidos
        </button>

        <button
          className={`nav-button ${
            activeTab === ProfileTab.FAVORITES ? "active" : ""
          }`}
          onClick={() => setActiveTab(ProfileTab.FAVORITES)}
        >
          Mis favoritos
        </button>
      </div>
    </div>
  );
};

export default UserProfileSidebar;
